import { getScanpayProcessedPaymentMethodKeys } from 'containers/invoices/utils';
import { ReceiptStatus, ReceiptsListPreset } from 'containers/receipts/receipts.model';
import {
  DISPUTE_IN_REVIEW_FUNNEL_STATUSES,
  DISPUTE_LOST_FUNNEL_STATUSES,
  DISPUTE_STATUS,
  DISPUTE_TAB_TITLE
} from '../constants';
import { DisputeListPreset } from '../disputes.model';
import { getDisputeTabFromStatuses } from '../utils';
import {
  DisputeReasonLine,
  DisputeReasonLineFilter,
  DisputeVisibility,
  DisputeWindow,
  DisputeZone
} from './disputeVisibility.model';

const EMPTY_FUNNEL = {
  total: 0,
  header: '',
  awaitingResponse: 0,
  won: 0,
  lost: 0,
  inReview: 0
};

export const DISPUTE_ZONES: DisputeZone[] = ['green', 'amber', 'red', 'grey'];

export const toDisputeZone = (value?: string | null): DisputeZone => {
  if (value && DISPUTE_ZONES.includes(value as DisputeZone)) return value as DisputeZone;
  return 'grey';
};

export const toUtcDateKey = (iso?: string | null): string | null => {
  if (!iso) return null;
  return iso.slice(0, 10);
};

export const resolveDisputeWindowRange = (days: number, start?: string | null, end?: string | null) => {
  if (start && end) return { start, end };
  const windowEnd = new Date();
  const windowStart = new Date(windowEnd.getTime() - days * 24 * 60 * 60 * 1000);
  return { start: windowStart.toISOString(), end: windowEnd.toISOString() };
};

const nonBlank = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const stringList = (value?: string[] | null): string[] => (Array.isArray(value) ? value.filter(Boolean) : []);

const normalizeReasonLines = (lines?: DisputeReasonLine[] | null): DisputeReasonLine[] => {
  if (!Array.isArray(lines)) return [];

  return lines.flatMap(line => {
    const text = nonBlank(line?.text);
    if (!text) return [];
    const filter = line.filter;
    return [{ text, filter: filter && typeof filter === 'object' ? filter : undefined }];
  });
};

export const disputeAwaitingResponseLine = (awaitingCount: number): string | null => {
  if (awaitingCount <= 0) return null;
  const label = awaitingCount === 1 ? 'dispute awaiting your response' : 'disputes awaiting your response';
  return `${awaitingCount} ${label}`;
};

export const normalizeDisputeVisibility = (raw: Partial<DisputeVisibility> | null | undefined): DisputeVisibility => {
  const windows = Array.isArray(raw?.windows) ? raw.windows : [];
  return {
    present: Boolean(raw?.present),
    rateTitle: nonBlank(raw?.rateTitle),
    chip: nonBlank(raw?.chip),
    zone: raw?.zone ? toDisputeZone(raw.zone) : undefined,
    awaitingCount: raw?.awaitingCount ?? 0,
    windows: windows.map(window => ({
      ...window,
      zone: toDisputeZone(window?.zone),
      chip: window?.chip ?? '',
      title: window?.title ?? '',
      paymentsCollected: window?.paymentsCollected ?? '',
      disputed: window?.disputed ?? '',
      disputeCount: window?.disputeCount ?? 0,
      belowFloor: Boolean(window?.belowFloor),
      days: window?.days ?? 0
    })),
    openDisputes: raw?.openDisputes,
    funnel: {
      ...EMPTY_FUNNEL,
      ...(raw?.funnel ?? {})
    },
    reasonLines: normalizeReasonLines(raw?.reasonLines),
    techniciansVisible: Boolean(raw?.techniciansVisible),
    concentration: raw?.concentration,
    technicians30: raw?.technicians30 ?? [],
    technicians90: raw?.technicians90 ?? [],
    reduceDisputesOpen: Boolean(raw?.reduceDisputesOpen),
    tips: raw?.tips ?? [],
    footer: raw?.footer
  };
};

export const windowRange = (disputeWindow: DisputeWindow) =>
  resolveDisputeWindowRange(disputeWindow.days, disputeWindow.windowStart, disputeWindow.windowEnd);

const buildPreset = ({
  tab,
  statuses,
  reasons = [],
  start,
  end,
  merchantId = null
}: {
  tab: DISPUTE_TAB_TITLE;
  statuses: DISPUTE_STATUS[];
  reasons?: string[];
  start?: string | null;
  end?: string | null;
  merchantId?: string | string[] | null;
}): DisputeListPreset => ({
  tab,
  statuses,
  reasons,
  startDisputedDate: toUtcDateKey(start),
  endDisputedDate: toUtcDateKey(end),
  exactDisputeDates: Boolean(start && end),
  exactDisputedStartDate: start ?? null,
  exactDisputedEndDate: end ?? null,
  merchantId
});

export const buildAllDisputesPreset = (start?: string | null, end?: string | null, merchantId?: string | null) =>
  buildPreset({
    tab: DISPUTE_TAB_TITLE.ALL_DISPUTES,
    statuses: [],
    start,
    end,
    merchantId
  });

export const buildPresetFromDisputeFilter = (filter: DisputeReasonLineFilter): DisputeListPreset => {
  const statuses = stringList(filter.status) as DISPUTE_STATUS[];
  const start = nonBlank(filter.startDisputedDate) ?? null;
  const end = nonBlank(filter.endDisputedDate) ?? null;
  const merchantIds = stringList(filter.merchantId);

  return {
    tab: getDisputeTabFromStatuses(statuses),
    statuses,
    reasons: stringList(filter.reason),
    startDisputedDate: null,
    endDisputedDate: null,
    exactDisputeDates: Boolean(start || end),
    exactDisputedStartDate: start,
    exactDisputedEndDate: end,
    merchantId: merchantIds.length ? merchantIds : null
  };
};

export const buildNeedsResponsePreset = (start?: string | null, end?: string | null) =>
  buildPreset({
    tab: DISPUTE_TAB_TITLE.NEEDS_RESPONSE,
    statuses: [DISPUTE_STATUS.NEEDS_RESPONSE],
    start,
    end
  });

export const buildWonPreset = (start?: string | null, end?: string | null) =>
  buildPreset({
    tab: DISPUTE_TAB_TITLE.WON,
    statuses: [DISPUTE_STATUS.WON],
    start,
    end
  });

export const buildLostPreset = (start?: string | null, end?: string | null) =>
  buildPreset({
    tab: DISPUTE_TAB_TITLE.LOST,
    statuses: DISPUTE_LOST_FUNNEL_STATUSES,
    start,
    end
  });

export const buildInReviewPreset = (start?: string | null, end?: string | null) =>
  buildPreset({
    tab: DISPUTE_TAB_TITLE.IN_REVIEW,
    statuses: DISPUTE_IN_REVIEW_FUNNEL_STATUSES,
    start,
    end
  });

export const buildReceiptsListPreset = (
  start?: string | null,
  end?: string | null,
  merchantId?: string | null
): ReceiptsListPreset => ({
  status: [ReceiptStatus.SUCCESS],
  startDate: toUtcDateKey(start),
  endDate: toUtcDateKey(end),
  createdBy: merchantId ? [merchantId] : [],
  paymentMethod: getScanpayProcessedPaymentMethodKeys(),
  exactCreatedAtFrom: start ?? null,
  exactCreatedAtTo: end ?? null
});
